import { React, useState, useEffect } from "react"
import { Form, Button, Card, Alert } from "react-bootstrap"
import axios from "axios"
import { Oval } from "react-loader-spinner"
import { BASE_URL } from "../../utils/constants.js"
import { useNavigate, useSearchParams } from "react-router-dom"

import "./SetPassword.scss"

const SetPassword = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [invitationValid, setInvitationValid] = useState(false)
  const [invitationData, setInvitationData] = useState(null)

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState(false)
  const [passwordMismatch, setPasswordMismatch] = useState(false)
  const [passwordTooShort, setPasswordTooShort] = useState(false)
  const [apiError, setApiError] = useState("")
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Password complexity requirements
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  })

  useEffect(() => {
    if (!token) {
      setLoading(false)
      setApiError("Geen uitnodigingstoken gevonden. Controleer de link in uw e-mail.")
      return
    }

    // Verify the invitation token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/v1/user/verify-invitation/${token}`)
        if (response.data.success) {
          setInvitationValid(true)
          setInvitationData(response.data.data)
          setLoading(false)
          setShowForm(true)
        }
      } catch (error) {
        console.log(error)
        setLoading(false)
        if (error.response && error.response.data && error.response.data.message) {
          setApiError(error.response.data.message)
        } else {
          setApiError("Er is een fout opgetreden bij het verifiëren van de uitnodiging")
        }
      }
    }

    verifyToken()
  }, [token])

  // Check password requirements as user types
  const checkPasswordRequirements = (pwd) => {
    setPasswordRequirements({
      minLength: pwd.length >= 12,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[@$!%*?&_\-#.,:;]/.test(pwd)
    })
  }

  const submitHandler = async event => {
    event.preventDefault()
    setError(false)
    setPasswordMismatch(false)
    setPasswordTooShort(false)
    setApiError("")

    // Validation
    if (password === "" || confirmPassword === "") {
      setError(true)
      return
    }

    // Check all password requirements
    const allRequirementsMet = Object.values(passwordRequirements).every(req => req === true)
    if (!allRequirementsMet) {
      setApiError("Wachtwoord voldoet niet aan alle vereisten")
      return
    }

    if (password !== confirmPassword) {
      setPasswordMismatch(true)
      return
    }

    setSubmitting(true)

    try {
      const response = await axios.post(`${BASE_URL}/api/v1/user/set-password`, {
        token: token,
        password: password,
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true // Send cookies with request
      })

      if (response.data.success) {
        setSuccess(true)
        // Store user info (without token) in localStorage
        localStorage.setItem('userInfo', JSON.stringify(response.data.user))
        setTimeout(() => {
          navigate("/dashboard")
          window.location.reload()
        }, 2000)
      }
    } catch (error) {
      console.log(error)
      setSubmitting(false)
      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message)
      } else {
        setApiError("Er is een fout opgetreden bij het aanmaken van uw account")
      }
    }
  }

  return (
    <div className="set-password">
      {loading ? (
        <div className="loading-container">
          <Oval
            height={80}
            width={80}
            color="#ffffff"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
            ariaLabel='oval-loading'
            secondaryColor="rgba(255, 255, 255, 0.5)"
            strokeWidth={3}
            strokeWidthSecondary={3}
          />
        </div>
      ) : success ? (
        <div className="message-card">
          <Card>
            <Card.Body>
              <h1 className="logo">OTOS</h1>
              <p className="subtitle">Account Aangemaakt</p>
              <Alert variant="success">
                <Alert.Heading>✓ Gelukt!</Alert.Heading>
                <p>
                  Uw wachtwoord is ingesteld en uw account is geactiveerd.
                </p>
                <p className="mb-0">
                  U wordt binnen enkele seconden doorgestuurd naar het dashboard...
                </p>
              </Alert>
            </Card.Body>
          </Card>
        </div>
      ) : (!invitationValid && apiError) ? (
        <div className="message-card">
          <Card>
            <Card.Body>
              <h1 className="logo">OTOS</h1>
              <p className="subtitle">Uitnodiging Verificatie</p>
              <Alert variant="danger">
                <Alert.Heading>⚠ Uitnodiging ongeldig</Alert.Heading>
                <p>{apiError}</p>
                <hr />
                <p className="mb-0">
                  Neem contact op met de systeembeheerder als u denkt dat dit een fout is.
                </p>
              </Alert>
            </Card.Body>
          </Card>
        </div>
      ) : showForm && invitationValid ? (
        <Form noValidate onSubmit={submitHandler}>
          <Card>
            <Card.Body>
              <h1 className="logo">OTOS</h1>
              <p className="subtitle">Account Activatie</p>
              <p>Stel uw wachtwoord in</p>

              {invitationData && (
                <div className="invitation-info">
                  <p><strong>Gebruikersnaam:</strong> {invitationData.username}</p>
                  <p><strong>E-mail:</strong> {invitationData.email}</p>
                </div>
              )}

              <Form.Group className="form-group">
                <Form.Control
                  style={{ border: (error && password === "") ? "2px solid red" : "" }}
                  type="password"
                  placeholder="Wachtwoord"
                  value={password}
                  onChange={(e) => {
                    setPasswordMismatch(false)
                    setPasswordTooShort(false)
                    setApiError("")
                    setPassword(e.target.value)
                    checkPasswordRequirements(e.target.value)
                  }}
                ></Form.Control>
                {error && password === "" ? <div className="text-danger"><small>Voer een wachtwoord in</small></div> : null}
              </Form.Group>

              {password && (
                <div className="password-requirements">
                  <p className="requirements-title">Wachtwoordvereisten:</p>
                  <ul>
                    <li className={passwordRequirements.minLength ? "met" : "not-met"}>
                      <span className="icon">{passwordRequirements.minLength ? "✓" : "○"}</span>
                      Minimaal 12 tekens
                    </li>
                    <li className={passwordRequirements.hasUppercase ? "met" : "not-met"}>
                      <span className="icon">{passwordRequirements.hasUppercase ? "✓" : "○"}</span>
                      Één hoofdletter
                    </li>
                    <li className={passwordRequirements.hasLowercase ? "met" : "not-met"}>
                      <span className="icon">{passwordRequirements.hasLowercase ? "✓" : "○"}</span>
                      Één kleine letter
                    </li>
                    <li className={passwordRequirements.hasNumber ? "met" : "not-met"}>
                      <span className="icon">{passwordRequirements.hasNumber ? "✓" : "○"}</span>
                      Één cijfer
                    </li>
                    <li className={passwordRequirements.hasSpecialChar ? "met" : "not-met"}>
                      <span className="icon">{passwordRequirements.hasSpecialChar ? "✓" : "○"}</span>
                      Één speciaal teken (@$!%*?&_-#.,:;)
                    </li>
                  </ul>
                </div>
              )}

              <Form.Group className="form-group">
                <Form.Control
                  style={{ border: (error && confirmPassword === "") || passwordMismatch ? "2px solid red" : "" }}
                  type="password"
                  placeholder="Bevestig wachtwoord"
                  value={confirmPassword}
                  onChange={(e) => {
                    setPasswordMismatch(false)
                    setConfirmPassword(e.target.value)
                  }}
                ></Form.Control>
                {error && confirmPassword === "" ? <div className="text-danger"><small>Bevestig uw wachtwoord</small></div> : null}
                {passwordMismatch ? <div className="text-danger"><small>Wachtwoorden komen niet overeen</small></div> : null}
              </Form.Group>

              {apiError && (
                <Alert variant="danger" className="mt-3">
                  <strong>Wachtwoord niet geaccepteerd</strong>
                  <p className="mb-2 mt-2">{apiError}</p>
                  <small>Controleer de bovenstaande vereisten en probeer opnieuw.</small>
                </Alert>
              )}

              <Button type="submit" variant="primary" disabled={submitting} className="w-100">
                {submitting ? "Bezig..." : "Account aanmaken"}
              </Button>
            </Card.Body>
          </Card>
        </Form>
      ) : null}
    </div>
  )
}

export default SetPassword
